import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useSupportPanel } from 'shared/contexts/SupportPanelContext';
import type { OnboardingHelpItem } from './onboardingHelp';

/**
 * The "common questions" card beside an onboarding step. A sidebar on wide
 * screens; below the form on narrow ones, where upstream used an end drawer
 * that was easy to miss. Answers start collapsed so the card stays shorter
 * than the form and the questions don't compete with its fields.
 */
export function OnboardingHelpPanel({ items }: { items: OnboardingHelpItem[] }) {
  const { openSupport } = useSupportPanel();

  return (
    <aside
      aria-labelledby="onboarding-help-heading"
      className="rounded-lg border border-slate-200 bg-white lg:sticky lg:top-24"
    >
      <h2
        id="onboarding-help-heading"
        className="border-b border-slate-200 px-4 py-3 text-base font-semibold text-slate-900"
      >
        Common questions
      </h2>
      {items.map((item) => (
        <details key={item.question} className="group border-b border-slate-100 last:border-b-0">
          <summary className="flex cursor-pointer list-none items-start justify-between gap-3 px-4 py-3 text-sm font-medium text-slate-800 hover:text-slate-900 group-open:text-slate-900 [&::-webkit-details-marker]:hidden">
            <span>{item.question}</span>
            <ChevronDown
              aria-hidden="true"
              className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180"
            />
          </summary>
          <p className="px-4 pb-4 text-sm text-slate-600">
            {item.answer}
            {item.link ? (
              <>
                {' '}
                <Link to={item.link.to} className="text-primary underline">
                  {item.link.label}
                </Link>
              </>
            ) : null}
          </p>
          {item.contactSupport ? (
            // Opens the message form directly, like the ID step's own
            // Contact support button; /support would leave onboarding.
            <div className="px-4 pb-4">
              <button
                type="button"
                onClick={() => openSupport('message')}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
              >
                Contact support
              </button>
            </div>
          ) : null}
        </details>
      ))}
    </aside>
  );
}
